import api from './client';
import type { BugReport, BugReportSeverity, BugReportStatus, BugReportType } from '../types';

export interface BugReportPayload {
  page_url: string;
  route_path?: string | null;
  comment?: string | null;
  console_logs?: string[] | null;
  device_info?: Record<string, unknown> | null;
  severity?: BugReportSeverity;
}

export interface BugReportListResponse {
  data: BugReport[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export async function fetchBugReports(params?: {
  status?: BugReportStatus | BugReportStatus[];
  severity?: BugReportSeverity;
  type?: BugReportType;
  user_id?: number;
  search?: string;
  page?: number;
  per_page?: number;
}): Promise<BugReportListResponse> {
  const response = await api.get('/api/v1/bug-reports', { params });
  return response.data;
}

export async function createBugReport(payload: BugReportPayload): Promise<{ data: BugReport; message: string }> {
  const response = await api.post('/api/v1/bug-reports', payload);
  return response.data;
}

export async function fetchBugReport(id: number): Promise<{ data: BugReport }> {
  const response = await api.get(`/api/v1/bug-reports/${id}`);
  return response.data;
}

export async function updateBugReportStatus(
  id: number,
  payload: {
    status: BugReportStatus;
    severity?: BugReportSeverity;
    type?: BugReportType;
    admin_note?: string | null;
  }
): Promise<{ data: BugReport; message: string }> {
  const response = await api.patch(`/api/v1/bug-reports/${id}/status`, payload);
  return response.data;
}

export async function fetchBugReportsSummary(): Promise<{
  new: number;
  in_progress: number;
  resolved: number;
  ignored: number;
  feature_requests: number;
  pending_execution: number;
}> {
  const response = await api.get('/api/v1/bug-reports/summary');
  return response.data;
}

export async function fetchPendingExecutionPlans(): Promise<{
  data: Array<{
    id: number;
    bug_report: BugReport;
    execution_plan: Record<string, unknown> | null;
    admin_execution_note: string | null;
    execution_plan_approved_at: string | null;
  }>;
}> {
  const response = await api.get('/api/v1/bug-reports/pending-execution');
  return response.data;
}

export async function deleteBugReport(id: number): Promise<{ message: string }> {
  const response = await api.delete(`/api/v1/bug-reports/${id}`);
  return response.data;
}

export async function processBugReportWithAi(
  id: number,
  dryRun = false
): Promise<{ data: BugReport; message: string }> {
  const response = await api.post(`/api/v1/bug-reports/${id}/process-ai`, { dry_run: dryRun });
  return response.data;
}

export async function processAllBugReportsWithAi(dryRun = false): Promise<{ message: string }> {
  const response = await api.post('/api/v1/bug-reports/process-ai', { dry_run: dryRun });
  return response.data;
}

export async function approveAiSuggestion(
  id: number,
  adminNote?: string | null
): Promise<{ data: BugReport; workflow: unknown; message: string }> {
  const response = await api.post(`/api/v1/bug-reports/${id}/approve-ai-suggestion`, {
    admin_note: adminNote || null,
  });
  return response.data;
}

export async function rejectAiSuggestion(
  id: number,
  reason?: string | null
): Promise<{ data: BugReport; workflow: unknown; message: string }> {
  const response = await api.post(`/api/v1/bug-reports/${id}/reject-ai-suggestion`, {
    reason: reason || null,
  });
  return response.data;
}

export async function approveExecutionPlan(
  id: number,
  adminNote?: string | null
): Promise<{ data: BugReport; workflow: unknown; message: string }> {
  const response = await api.post(`/api/v1/bug-reports/${id}/approve-execution-plan`, {
    admin_note: adminNote || null,
  });
  return response.data;
}

export async function rejectExecutionPlan(
  id: number,
  reason?: string | null
): Promise<{ data: BugReport; workflow: unknown; message: string }> {
  const response = await api.post(`/api/v1/bug-reports/${id}/reject-execution-plan`, {
    reason: reason || null,
  });
  return response.data;
}
